import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { 
  Tag, Plus, Edit2, Trash2, X, Loader2, Save, DollarSign, MapPin, 
  Calendar, Eye, EyeOff, Copy, Check, Clock, Percent
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSpecialOffers = () => {
  const { toast } = useToast();
  const [filter, setFilter] = useState('all');
  
  // Offers state
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Locations & treatments
  const [locations, setLocations] = useState([]);
  const [treatments, setTreatments] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    title: '', promoCode: '', description: '', discountType: 'percentage', discountValue: '',
    visibility: 'public', redeemableType: 'all', applicableTreatments: [],
    locationId: '', usageLimit: '', perCustomerLimit: '1', minimumPurchase: '',
    startDate: '', endDate: '', status: 'active', active: true
  });

  useEffect(() => {
    fetchLocations();
    fetchTreatments();
    fetchOffers();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${API}/locations?practiceId=demo-practice-001`);
      const data = await response.json();
      setLocations(data);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const fetchTreatments = async () => {
    try {
      const response = await fetch(`${API}/treatments?practiceId=demo-practice-001`);
      const data = await response.json();
      setTreatments(data);
    } catch (err) {
      console.error('Error fetching treatments:', err);
    }
  };

  const fetchOffers = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/special-offers?practiceId=demo-practice-001`);
      const data = await response.json();
      setOffers(data);
    } catch (err) {
      console.error('Error fetching offers:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredOffers = offers.filter(o => {
    if (filter === 'all') return true;
    return o.status === filter;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...formData,
        discountValue: parseFloat(formData.discountValue) || 0,
        usageLimit: parseInt(formData.usageLimit) || 0,
        perCustomerLimit: parseInt(formData.perCustomerLimit) || 1,
        minimumPurchase: parseFloat(formData.minimumPurchase) || 0,
        practiceId: 'demo-practice-001'
      };

      const url = editingOffer ? `${API}/special-offers/${editingOffer.id}` : `${API}/special-offers`;
      const method = editingOffer ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save offer');
      }
      
      toast({ title: editingOffer ? 'Offer Updated' : 'Offer Created', description: `${formData.title} has been saved.` });
      resetForm();
      fetchOffers();
    } catch (err) {
      toast({ title: 'Error', description: err.message || 'Failed to save offer.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setFormData({
      title: offer.title || '', promoCode: offer.promoCode || '', description: offer.description || '',
      discountType: offer.discountType || 'percentage', discountValue: offer.discountValue?.toString() || '',
      visibility: offer.visibility || 'public', redeemableType: offer.redeemableType || 'all',
      applicableTreatments: offer.applicableTreatments || [], locationId: offer.locationId || '',
      usageLimit: offer.usageLimit?.toString() || '', perCustomerLimit: offer.perCustomerLimit?.toString() || '1',
      minimumPurchase: offer.minimumPurchase?.toString() || '', startDate: offer.startDate || '',
      endDate: offer.endDate || '', status: offer.status || 'active', active: offer.active !== false
    });
    setShowForm(true);
  };

  const handleActivate = async (offer) => {
    try {
      await fetch(`${API}/special-offers/${offer.id}/activate`, { method: 'PUT' });
      toast({ title: 'Offer Activated' });
      fetchOffers();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to activate offer.', variant: 'destructive' });
    }
  };

  const handleDelete = async (offer) => {
    if (!window.confirm(`Delete "${offer.title}"? This cannot be undone.`)) return;
    try {
      await fetch(`${API}/special-offers/${offer.id}`, { method: 'DELETE' });
      toast({ title: 'Deleted', description: `${offer.title} has been deleted.` });
      fetchOffers();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete offer.', variant: 'destructive' });
    }
  };

  const copyPromoCode = (code) => {
    navigator.clipboard.writeText(code);
    toast({ title: 'Copied!', description: `Promo code "${code}" copied to clipboard.` });
  };

  const resetForm = () => {
    setFormData({
      title: '', promoCode: '', description: '', discountType: 'percentage', discountValue: '',
      visibility: 'public', redeemableType: 'all', applicableTreatments: [],
      locationId: '', usageLimit: '', perCustomerLimit: '1', minimumPurchase: '',
      startDate: '', endDate: '', status: 'active', active: true
    });
    setEditingOffer(null);
    setShowForm(false);
  };

  const getLocationName = (locationId) => {
    if (!locationId) return 'All Locations';
    const loc = locations.find(l => l.id === locationId);
    return loc ? loc.name : 'Unknown';
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || styles.draft}>{status}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="admin-special-offers">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Tag className="h-6 w-6 text-purple-500" />
            Special Offers
          </h1>
          <p className="text-gray-600">Create and manage promotional offers and discount codes</p>
        </div>
        <Button onClick={() => setShowForm(true)} data-testid="create-offer-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Offer
        </Button>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-5 gap-4">
        <Card className={`cursor-pointer ${filter === 'all' ? 'ring-2 ring-primary' : ''}`} onClick={() => setFilter('all')}>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">All Offers</p>
            <p className="text-2xl font-bold">{offers.length}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer ${filter === 'active' ? 'ring-2 ring-primary' : ''}`} onClick={() => setFilter('active')}>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Active</p>
            <p className="text-2xl font-bold text-green-600">{offers.filter(o => o.status === 'active').length}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer ${filter === 'scheduled' ? 'ring-2 ring-primary' : ''}`} onClick={() => setFilter('scheduled')}>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Scheduled</p>
            <p className="text-2xl font-bold text-blue-600">{offers.filter(o => o.status === 'scheduled').length}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer ${filter === 'expired' ? 'ring-2 ring-primary' : ''}`} onClick={() => setFilter('expired')}>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Expired</p>
            <p className="text-2xl font-bold text-red-600">{offers.filter(o => o.status === 'expired').length}</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer ${filter === 'draft' ? 'ring-2 ring-primary' : ''}`} onClick={() => setFilter('draft')}>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Draft</p>
            <p className="text-2xl font-bold text-gray-600">{offers.filter(o => o.status === 'draft').length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Offers List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : filteredOffers.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="text-center py-12">
            <Tag className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No special offers</h3>
            <p className="text-gray-500 mb-4">Create your first promotional offer</p>
            <Button onClick={() => setShowForm(true)}>
              <Plus className="h-4 w-4 mr-2" />Create Offer
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredOffers.map((offer) => (
            <Card key={offer.id} className={offer.status === 'expired' ? 'opacity-60' : ''}>
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle className="text-lg">{offer.title}</CardTitle>
                    {offer.promoCode && (
                      <div className="flex items-center gap-2 mt-2">
                        <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">{offer.promoCode}</code>
                        <Button variant="ghost" size="sm" onClick={() => copyPromoCode(offer.promoCode)}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  {getStatusBadge(offer.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {/* Discount */}
                  <div className="flex items-center justify-between p-2 bg-purple-50 rounded">
                    <span className="text-sm text-purple-700 flex items-center gap-1">
                      <Percent className="h-4 w-4" />
                      Discount
                    </span>
                    <span className="font-bold text-purple-800">
                      {offer.discountType === 'percentage' ? `${offer.discountValue}%` : `$${offer.discountValue}`} off
                    </span>
                  </div>

                  {/* Details */}
                  <div className="space-y-1 text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="h-3 w-3" />
                      {getLocationName(offer.locationId)}
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                      {offer.visibility === 'public' ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      {offer.visibility === 'public' ? 'Public' : offer.visibility === 'members_only' ? 'Members Only' : 'Hidden'}
                    </div>
                    {(offer.startDate || offer.endDate) && (
                      <div className="flex items-center gap-2 text-gray-600">
                        <Calendar className="h-3 w-3" />
                        {offer.startDate || 'Now'} - {offer.endDate || 'No end'}
                      </div>
                    )}
                  </div>

                  {/* Usage */}
                  <div className="flex items-center gap-2 pt-2 border-t">
                    <Badge variant="secondary" className="text-xs">
                      Used: {offer.usageCount || 0}{offer.usageLimit > 0 ? `/${offer.usageLimit}` : ''}
                    </Badge>
                    {offer.minimumPurchase > 0 && (
                      <Badge variant="secondary" className="text-xs">Min: ${offer.minimumPurchase}</Badge>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex justify-between items-center pt-2">
                    {offer.status === 'draft' && (
                      <Button size="sm" variant="outline" onClick={() => handleActivate(offer)}>
                        <Check className="h-3 w-3 mr-1" />Activate
                      </Button>
                    )}
                    {offer.status !== 'draft' && <div />}
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(offer)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(offer)} className="text-red-500 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>{editingOffer ? 'Edit Special Offer' : 'Create Special Offer'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetForm}><X className="h-4 w-4" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sTitle">Offer Title *</Label>
                    <Input id="sTitle" value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} placeholder="e.g., Summer Special" required className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="sPromo">Promo Code</Label>
                    <Input id="sPromo" value={formData.promoCode} onChange={(e) => setFormData({...formData, promoCode: e.target.value.toUpperCase()})} placeholder="e.g., SUMMER20" className="mt-1 uppercase" />
                    <p className="text-xs text-gray-500 mt-1">Leave blank for auto-applied offers</p>
                  </div>
                </div>

                <div>
                  <Label htmlFor="sDesc">Description</Label>
                  <Textarea id="sDesc" value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} placeholder="Describe the offer..." rows={2} className="mt-1" />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="sDiscountValue">Discount Value *</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input id="sDiscountValue" type="number" value={formData.discountValue} onChange={(e) => setFormData({...formData, discountValue: e.target.value})} placeholder="20" required className="pl-10" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="sDiscountType">Discount Type</Label>
                    <select id="sDiscountType" value={formData.discountType} onChange={(e) => setFormData({...formData, discountType: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                      <option value="percentage">Percentage Off</option>
                      <option value="fixed">Fixed Amount</option>
                      <option value="credit">Account Credit</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="sVisibility">Visibility</Label>
                    <select id="sVisibility" value={formData.visibility} onChange={(e) => setFormData({...formData, visibility: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                      <option value="public">Public</option>
                      <option value="hidden">Hidden (code required)</option>
                      <option value="members_only">Members Only</option>
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sRedeemable">Redeemable On</Label>
                    <select id="sRedeemable" value={formData.redeemableType} onChange={(e) => setFormData({...formData, redeemableType: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                      <option value="all">All Services & Products</option>
                      <option value="treatments">Treatments Only</option>
                      <option value="products">Products Only</option>
                      <option value="memberships">Memberships Only</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="sLocation">Location</Label>
                    <select id="sLocation" value={formData.locationId} onChange={(e) => setFormData({...formData, locationId: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                      <option value="">All Locations</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="sUsageLimit">Total Usage Limit</Label>
                    <Input id="sUsageLimit" type="number" value={formData.usageLimit} onChange={(e) => setFormData({...formData, usageLimit: e.target.value})} placeholder="0 = unlimited" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="sPerCustomer">Per Customer Limit</Label>
                    <Input id="sPerCustomer" type="number" value={formData.perCustomerLimit} onChange={(e) => setFormData({...formData, perCustomerLimit: e.target.value})} placeholder="1" className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="sMinPurchase">Minimum Purchase ($)</Label>
                    <Input id="sMinPurchase" type="number" value={formData.minimumPurchase} onChange={(e) => setFormData({...formData, minimumPurchase: e.target.value})} placeholder="0" className="mt-1" />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sStart">Start Date</Label>
                    <Input id="sStart" type="date" value={formData.startDate} onChange={(e) => setFormData({...formData, startDate: e.target.value})} className="mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="sEnd">End Date</Label>
                    <Input id="sEnd" type="date" value={formData.endDate} onChange={(e) => setFormData({...formData, endDate: e.target.value})} className="mt-1" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="sStatus">Status</Label>
                  <select id="sStatus" value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                    <option value="draft">Draft (save without activating)</option>
                    <option value="active">Active (start immediately)</option>
                    <option value="scheduled">Scheduled (starts on start date)</option>
                  </select>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />{editingOffer ? 'Update' : 'Create'} Offer</>}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminSpecialOffers;
